/**
 * Types du domaine facturation.
 */
import type { ClientSummary, QuoteLine } from "../quotes/types.js";
import { round2 } from "../../money.js";

export type InvoiceStatus =
  | "draft"
  | "sent"
  | "partially_paid"
  | "paid"
  | "overdue"
  | "disputed"
  | "cancelled"
  | "credit_note_issued";

export type InvoiceType = "invoice" | "deposit" | "situation" | "credit_note";

/**
 * Avancement de la transmission à la plateforme de facturation électronique.
 *
 * Distinct du statut de paiement : une facture peut être acceptée par Chorus
 * Pro et rester impayée. Les valeurs suivent exactement la contrainte
 * `invoices_einvoice_status_check` — une liste plus courte côté application
 * ferait échouer l'écriture sans que le code ne le laisse voir.
 */
export type EInvoiceStatus =
  | "not_sent"
  | "generating"
  | "ready"
  | "submitted_to_pa"
  | "accepted_by_pa"
  | "rejected_by_pa"
  | "delivered_to_buyer"
  | "acknowledged"
  | "refused_by_buyer"
  | "chorus_submitted"
  | "chorus_accepted"
  | "chorus_rejected";

export interface Payment {
  id: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string | null;
  reference: string | null;
  notes: string | null;
}

export interface ReminderRef {
  id: string;
  channel: string;
  status: string;
  scheduledAt: string;
  sentAt: string | null;
  subject: string | null;
}

export interface InvoiceSummary {
  id: string;
  invoiceNumber: string;
  type: InvoiceType;
  title: string;
  status: InvoiceStatus;
  client: ClientSummary | null;
  totalHt: number;
  totalTva: number;
  totalTtc: number;
  amountPaid: number;
  /**
   * Acomptes et situations du même devis déjà facturés quand celle-ci a été
   * émise. Zéro partout ailleurs que sur une facture de solde.
   *
   * Figé en base : un acompte annulé plus tard ne doit pas changer ce qu'un
   * solde déjà envoyé disait au client.
   */
  priorInvoicedHt: number;
  priorInvoicedTtc: number;
  dueDate: string;
  einvoiceStatus: EInvoiceStatus;
  einvoiceSubmittedAt: string | null;
  /** Voir `computeBalance`. */
  balance: InvoiceBalance;
}

export interface InvoiceDetail extends InvoiceSummary {
  quoteId: string | null;
  projectId: string | null;
  payments: Payment[];
  reminders: ReminderRef[];

  /**
   * Le détail facturé.
   *
   * Ce sont les lignes du **devis** dont la facture découle : il n'existe pas
   * de table `invoice_lines`, et il n'en faut pas — une facture de travaux
   * facture le marché, et le marché est le devis. Les inventer ailleurs
   * donnerait deux versions du même détail, qui divergeraient au premier
   * avenant.
   *
   * Vide pour une facture sans devis : elle n'a jamais eu de détail, et le
   * dire vaut mieux qu'un tableau blanc.
   */
  lines: QuoteLine[];
  /** Le devis facturé, pour que le document le cite. */
  quoteNumber: string | null;
  /** Le marché entier, base de calcul d'un acompte ou d'une situation. */
  quoteTotalTtc: number | null;
  /**
   * Le pourcentage porté par cette facture. Son sens vient du `type` :
   * fraction du marché pour un acompte, avancement pour une situation, null
   * pour une finale ou un avoir — un seul champ plutôt que deux dont un est
   * toujours vide.
   */
  percent: number | null;
}

export interface InvoiceBalance {
  /**
   * Le net à payer du document : le total, moins ce qui a déjà été facturé.
   *
   * C'est le modèle français du solde — la facture finale reprend le marché
   * entier et déduit les acomptes — et c'est celui que `computeInvoicedHt`
   * suppose déjà. Sans cette soustraction, un solde réclamait 100 % après un
   * acompte de 30 %.
   */
  netToPay: number;
  /** Ce qu'il reste à encaisser sur le net à payer. Jamais négatif. */
  remaining: number;
  /** Trop-perçu, à rembourser ou à imputer. Zéro dans le cas normal. */
  overpaid: number;
  isSettled: boolean;
  isOverdue: boolean;
  /** Jours de retard. Zéro si l'échéance n'est pas dépassée. */
  daysLate: number;
}

/**
 * Plus rien n'est attendu du client sur cette facture.
 *
 * Definie ici parce que ce module possede `InvoiceStatus`. L'encours d'un
 * client posait la meme regle dans sa propre copie, mot pour mot : deux
 * exemplaires d'une regle metier finissent par diverger sans que rien ne le
 * signale.
 */
const SETTLED = new Set<InvoiceStatus>(["paid", "cancelled", "credit_note_issued"]);

export function isSettledStatus(status: InvoiceStatus): boolean {
  return SETTLED.has(status);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Solde d'une facture.
 *
 * On raisonne sur les montants plutôt que sur le statut : un statut se met à
 * jour par trigger ou par cron, donc il peut retarder d'une nuit sur la
 * réalité. Le montant encaissé, lui, est exact à la seconde où on le lit.
 */
export function computeBalance(
  invoice: Pick<
    InvoiceSummary,
    "totalTtc" | "amountPaid" | "dueDate" | "status" | "priorInvoicedTtc"
  >,
  today: Date = new Date(),
): InvoiceBalance {
  // Le net à payer d'abord, l'encaissé ensuite. Les deux soustractions ne se
  // confondent pas : la première dit ce que le document réclame, la seconde ce
  // qu'il en reste. Un solde de 12 000 € sur un marché de 12 000 € dont
  // 3 600 € d'acompte réclame 8 400 €, encaissés ou non.
  const netToPay = round2(invoice.totalTtc - (invoice.priorInvoicedTtc ?? 0));
  const diff = round2(netToPay - invoice.amountPaid);
  const remaining = Math.max(0, diff);
  const overpaid = Math.max(0, -diff);

  const isSettled = isSettledStatus(invoice.status) || remaining === 0;

  const todayIso = today.toISOString().slice(0, 10);
  const late = !isSettled && invoice.dueDate < todayIso;

  const daysLate = late
    ? Math.max(
        0,
        Math.floor(
          (Date.parse(`${todayIso}T00:00:00Z`) - Date.parse(`${invoice.dueDate}T00:00:00Z`)) /
            DAY_MS,
        ),
      )
    : 0;

  return { netToPay, remaining, overpaid, isSettled, isOverdue: late, daysLate };
}

/**
 * Le statut à montrer, qui n'est pas toujours celui de la base.
 *
 * `overdue` est posé par un cron de nuit. Une facture échue depuis la veille
 * porte donc encore `sent` alors qu'elle est en retard — et l'écran, s'il
 * recopie le statut, annonce « Envoyée » à côté d'une date dépassée. La date,
 * elle, ne se trompe jamais d'un jour : c'est déjà le raisonnement
 * qu'`invoices_due_for_reminder` tient en base.
 *
 * Seul `sent` est corrigé. Une facture partiellement payée garde son « acompte
 * reçu », qui dit quelque chose que le retard ne dit pas ; un brouillon échu
 * n'est pas en retard, il n'est jamais parti.
 */
export function effectiveStatus(
  invoice: Pick<InvoiceSummary, "status" | "balance">,
): InvoiceStatus {
  return invoice.status === "sent" && invoice.balance.isOverdue ? "overdue" : invoice.status;
}

export interface Receivables {
  /** Facturé et non encaissé, échéance non dépassée comprise. */
  totalDue: number;
  /** La part échue. C'est le chiffre qui doit faire réagir. */
  overdueDue: number;
  overdueCount: number;
  /**
   * La part portée par des factures encore en brouillon — donc jamais
   * envoyées au client.
   *
   * Elle reste comptée dans `totalDue` : une facture peut avoir été remise en
   * main propre ou par un autre canal, et la retirer d'office ferait
   * disparaître de l'argent réellement dû. Mais elle n'est pas de même nature
   * que le reste, et le circuit de relance l'ignore — `mark_invoices_overdue`
   * comme `invoices_due_for_reminder` ne regardent que ce qui est parti. Sans
   * ce chiffre, le tableau de bord et la relance répondraient différemment à
   * la même question sans que rien ne le dise.
   */
  unsentDue: number;
  unsentCount: number;
  /**
   * La part de `overdueDue` qui vient de brouillons.
   *
   * C'est le nombre réellement trompeur. « Échu » veut dire « le client est en
   * retard » ; sur une facture jamais envoyée, le retard est celui de
   * l'entreprise, et aucune relance ne partira jamais pour le rattraper.
   */
  unsentOverdueDue: number;
}

/**
 * Total dû sur un ensemble de factures, et part en retard.
 *
 * C'est le chiffre du tableau de bord : « l'argent qui dort ».
 */
export function computeReceivables(invoices: readonly InvoiceSummary[]): Receivables {
  let totalDue = 0;
  let overdueDue = 0;
  let overdueCount = 0;
  let unsentDue = 0;
  let unsentCount = 0;
  let unsentOverdueDue = 0;

  for (const invoice of invoices) {
    if (invoice.balance.isSettled) continue;
    const { remaining, isOverdue } = invoice.balance;

    totalDue += remaining;

    // Le brouillon est le seul état que l'application contrôle de bout en
    // bout : c'est elle qui le pose à la création et qui l'enlève à l'envoi.
    // Se fonder sur `document_sends` dirait autre chose — qu'aucun courrier
    // n'est parti d'ici — ce qui est vrai d'une facture remise à la main.
    const jamaisEnvoyee = invoice.status === "draft";
    if (jamaisEnvoyee) {
      unsentDue += remaining;
      unsentCount += 1;
    }

    if (isOverdue) {
      overdueDue += remaining;
      overdueCount += 1;
      if (jamaisEnvoyee) unsentOverdueDue += remaining;
    }
  }

  return {
    totalDue: round2(totalDue),
    overdueDue: round2(overdueDue),
    overdueCount,
    unsentDue: round2(unsentDue),
    unsentCount,
    unsentOverdueDue: round2(unsentOverdueDue),
  };
}
